import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Lock, BookOpen, Wallet, ReceiptText, BarChart3, ShoppingCart, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("BE0788952072");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/hub" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(identifier, password);
      navigate("/hub");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden"
           style={{ background: "linear-gradient(160deg,#0b1e4a 0%,#123a8f 55%,#1d4ed8 100%)" }}>
        {/* subtiele rasteroverlay voor diepte */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.08]"
             style={{ backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)", backgroundSize: "44px 44px" }} />
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-blue-400/20 blur-3xl" />

        <div className="relative flex items-center gap-3 animate-in fade-in slide-in-from-top-4 duration-700">
          <div className="h-11 w-11 rounded-md bg-white/15 flex items-center justify-center backdrop-blur">
            <Building2 className="h-6 w-6" />
          </div>
          <div>
            <div className="font-heading font-semibold text-lg flex items-center gap-2">
              Singh Express Shop BV
              <span className="text-[10px] font-medium tracking-widest text-white/60 border border-white/25 rounded-full px-1.5 py-0.5" data-testid="login-est-badge">EST. 2022</span>
            </div>
            <div className="text-sm text-white/70">BE 0788.952.072</div>
          </div>
        </div>

        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1 text-xs text-white/80 mb-5 animate-in fade-in slide-in-from-left-4 duration-700">
            <ShieldCheck className="h-3.5 w-3.5" /> Boekhoud- &amp; beheerplatform
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-tight animate-in fade-in slide-in-from-left-4 duration-700"
              style={{ animationDelay: "80ms" }}>
            Boekhouding<br />&amp; bedrijfsbeheer
          </h1>
          <p className="mt-5 text-white/75 max-w-md text-base leading-relaxed animate-in fade-in duration-700"
             style={{ animationDelay: "160ms" }}>
            Dagboek, kasboek, factuurboek, grootboek, rapporten én aankopen — alles op één plek.
            Conform FOD Financiën Circulaire 2020/C/20, met onveranderlijke boekingen en een
            cryptografische audit trail.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-3 max-w-md">
            {[
              { icon: BookOpen, label: "Dagboek van ontvangsten" },
              { icon: Wallet, label: "Kasboek & kasafstemming" },
              { icon: ReceiptText, label: "Factuurboek & grootboek" },
              { icon: BarChart3, label: "BTW-rapporten & marges" },
              { icon: ShoppingCart, label: "Aankopen rookwaren" },
              { icon: ShieldCheck, label: "Audit trail (10 jaar)" },
            ].map((f, i) => (
              <div key={f.label}
                   className="flex items-center gap-2.5 text-sm text-white/85 animate-in fade-in slide-in-from-bottom-3 duration-700"
                   style={{ animationDelay: `${240 + i * 70}ms` }}>
                <span className="h-7 w-7 shrink-0 rounded-md bg-white/10 flex items-center justify-center">
                  <f.icon className="h-3.5 w-3.5" />
                </span>
                {f.label}
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-xs text-white/50">Beveiligde toegang · Rolgebaseerde rechten · Onveranderlijke boekingen</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
              <Building2 className="h-5 w-5" />
            </div>
            <span className="font-heading font-semibold">Singh Express Shop</span>
          </div>
          <h2 className="font-heading text-2xl font-medium tracking-tight">Aanmelden</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Meld u aan om verder te gaan.</p>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="identifier">BTW-nummer</Label>
              <Input id="identifier" type="text" data-testid="login-email" value={identifier}
                     onChange={(e) => setIdentifier(e.target.value)} required autoComplete="username"
                     placeholder="BE0788952072" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Wachtwoord</Label>
              <Input id="password" type="password" data-testid="login-password" value={password}
                     onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button type="submit" data-testid="login-submit" disabled={loading}
                    className="w-full active:scale-[0.98] transition-transform">
              <Lock className="h-4 w-4 mr-2" />
              {loading ? "Bezig…" : "Aanmelden"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
